import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageSquareText, Send, X } from "lucide-react";
import { API } from "../lib/api";
import { useLang } from "../lib/i18n";

const sid = () => {
  let s = localStorage.getItem("ogo_chat_sid");
  if (!s) { s = `web_${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`; localStorage.setItem("ogo_chat_sid", s); }
  return s;
};

export default function ChatWidget() {
  const { a } = useLang();
  const d = a.chat;
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const sessionId = useRef(sid());

  useEffect(() => {
    if (!open || msgs.length) return;
    fetch(`${API}/chat/${sessionId.current}`).then((r) => r.json()).then((h) => setMsgs(h.map((m) => ({ role: m.role, content: m.content })))).catch(() => {});
  }, [open, msgs.length]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sessionId.current, message: text }) });
      if (!res.ok) throw new Error("Chat failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const chunk = p.slice(6);
          if (chunk === "[DONE]") continue;
          const textChunk = chunk.replace(/\\n/g, "\n");
          setMsgs((m) => { const n = [...m]; n[n.length - 1] = { ...n[n.length - 1], content: n[n.length - 1].content + textChunk }; return n; });
        }
      }
    } catch {
      setMsgs((m) => { const n = [...m]; n[n.length - 1] = { role: "assistant", content: "Sorry — please try again or WhatsApp us." }; return n; });
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="chat-open-button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Chat with OGO Assistant"
        className="fixed bottom-5 right-5 z-[80] h-14 w-14 rounded-full bg-gold text-ink shadow-[0_10px_40px_rgba(230,194,128,0.35)] flex items-center justify-center hover:scale-105 transition-transform"
      >
        {open ? <X size={22} /> : <MessageSquareText size={22} />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-5 z-[80] w-[calc(100vw-2.5rem)] max-w-sm rounded-3xl border border-white/10 bg-panel/95 backdrop-blur-xl shadow-2xl flex flex-col overflow-hidden"
            style={{ height: "min(560px, calc(100vh - 8rem))" }}
          >
            <div className="px-5 py-4 border-b border-white/10">
              <p className="font-display font-bold text-white">{d.title}</p>
              <p className="text-slate-400 text-xs">{d.sub}</p>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              <Bubble role="assistant">{d.hello}</Bubble>
              {msgs.map((m, i) => <Bubble key={i} role={m.role} testid={`chat-msg-${i}`}>{m.content || (busy && i === msgs.length - 1 ? "…" : "")}</Bubble>)}
              <div ref={endRef} />
            </div>
            <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
              <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder={d.placeholder} className="flex-1 rounded-full border border-white/15 bg-ink/60 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-gold/60" />
              <button data-testid="chat-send-button" type="submit" disabled={busy || !input.trim()} className="h-10 w-10 rounded-full bg-gold text-ink flex items-center justify-center disabled:opacity-40"><Send size={16} /></button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

const Bubble = ({ role, children, testid }) => (
  <div data-testid={testid} className={`flex ${role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${role === "user" ? "bg-gold text-ink rounded-br-md" : "bg-ink/70 border border-white/10 text-slate-200 rounded-bl-md"}`}>
      {children}
    </div>
  </div>
);
